import { CustomDateFormats, noddiFormatDate } from 'noddi-util';

import { NoddiIconButton } from '../../Inputs/NoddiIconButton';
import { NoddiSelect } from '../../Inputs/NoddiSelect/NoddiSelect';

interface Props {
  /** Any day within the selected month. */
  value: Date;
  /** Receives the first day of the chosen month. */
  onChange: (month: Date) => void;
  /** How many past months the jump dropdown lists (default 24). */
  monthsBack?: number;
}

// Comparable per-month ordinal (year * 12 + monthIndex).
const monthOrdinal = (date: Date) => date.getFullYear() * 12 + date.getMonth();
// 'yyyy-MM' — stable, sortable, round-trips through the jump dropdown.
const monthKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

/**
 * Compact month stepper for monthly reports: ‹ prev | June 2026 (jump) | next ›.
 * Stepping past the current month is blocked — future months have no data.
 */
export const MonthNavigator = ({ value, onChange, monthsBack = 24 }: Props) => {
  const now = new Date();
  const atCurrentMonth = monthOrdinal(value) >= monthOrdinal(now);

  const step = (delta: number) => onChange(new Date(value.getFullYear(), value.getMonth() + delta, 1));

  const options = Array.from({ length: monthsBack }, (_, offset) => {
    const month = new Date(now.getFullYear(), now.getMonth() - offset, 1);
    return { label: noddiFormatDate(month, CustomDateFormats.FULL_MONTH_AND_YEAR), value: monthKey(month) };
  });

  return (
    <div className='flex items-center gap-2'>
      <NoddiIconButton iconName='ChevronLeft' variant='outline' onClick={() => step(-1)} aria-label='Previous month' />
      <div className='min-w-40'>
        <NoddiSelect
          clearable={false}
          options={options}
          value={monthKey(value)}
          onChange={(key) => {
            if (key) {
              const [year, month] = key.split('-').map(Number);
              onChange(new Date(year!, month! - 1, 1));
            }
          }}
        />
      </div>
      <NoddiIconButton
        iconName='ChevronRight'
        variant='outline'
        disabled={atCurrentMonth}
        onClick={() => step(1)}
        aria-label='Next month'
      />
    </div>
  );
};
